import { NavLink, useNavigate, useParams } from 'react-router-dom'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import buttonStyles from '../../../components/Button/Button.module.scss'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { CreateInputParametersHierarchyNode, useCreateNodeMutation, useLazyGetEffectiveNodeQuery } from '../../../store/ai/inputParametersHierarchy'
import {
  MeetingTemplatePreset,
  useCreateMeetingTemplateMutation,
  useGetDefaultMeetingTemplateQuery,
  useGetMeetingTemplateByIdQuery,
  useSetUserDefaultMeetingTemplateMutation,
  useUpdateMeetingTemplateMutation,
} from '../../../store/ai/meetingTemplate'
import { Breadcrumb, Col, Container, Form, OverlayTrigger, Row, Spinner, Stack, Tab, Table, Tooltip as BootstrapTooltip } from 'react-bootstrap'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'
import { useGetFeaturesQuery, useGetPresetsQuery } from '../../../store/ai/ai'
import { AddMeetingTemplatePresetModal } from './AddMeetingTemplatePresetModal'
import styled from 'styled-components'
import { Button } from '../../../components/Button/Button'
import { MultiScopedBlock, ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import classNames from 'classnames'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { UpdateSystemMeetingTemplateModal } from './UpdateSystemMeetingTemplateModal'
import { TagElement } from '../../../components/Tag/Tag'
import { useZocksAuth, useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { getMeetingTemplatePresetMetadata, MEETING_TEMPLATE_COLUMNS } from './utils'
import { useUpdateMeetingTemplateMetadata } from './hooks'
import { AiFeaturePreset } from '../../../models/AiFeaturePreset'
import { MeetingTemplatePresetFormConfigModal } from '../AIFeaturePresets/PresetConfig/Form/MeetingTemplatePresetFormConfigModal'
import PrizeBadge from '../../../components/ScopedBlock/PrizeBadge.svg?url'
import { InputParameterHierarchyType } from '../../../components/InputParameterHierarchy/model'
import './MeetingTemplate.scss'
import { useGetUserByIdQuery, useSaveUserByIdMutation } from '../../../store/user/user'
import { DefaultUserMeetingTemplateModal } from './DefaultUserMeetingTemplateModal'
import { useGetAccountQuery } from '../../../store/accountUserSettings/accountUserSettings'
import { Tabs } from '../../../components/Tabs/Tabs'
import { AiFeature } from '../../../models/AiFeature'
import { AddMeetingTemplateFormModal } from './AddMeetingTemplateFormModal'
import { useGetAllScorecardsQuery } from '../../../store/ai/scorecard'
import { LoadingResult } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/ResultCard/Body/LoadingResult'
import { MEETING_SCORE_PRESET_PRESET_ID } from '../../../apps/professional-app/fa/MeetingSummary/MeetingScorecard/model'
import { MeetingAgendaInputParametersHierarchyConfigModal } from '../../../components/InputParameterHierarchy/MeetingAgendaInputParametersHierarchyConfigModal'
import { useAgendaInputHierarchyDefaults } from '../../../components/InputParameterHierarchy/hooks'
import { useAgendaFeature } from '../../../apps/professional-app/fa/MeetingPreparation/Agenda/useAgendaFeature'
import { UpdateMeetingTemplatePrivacyModal } from './UpdateMeetingTemplatePrivacyModal'
import { usePrivateMeetingsEnabled } from '../../../common/usePrivateMeetingsEnabled'
import { AIFeaturePresetBusinessEvents } from '../AIFeaturePresets/AIFeaturePresetBusinessEvents'
import { InputParametersHierarchyId, InputParametersHierarchyLevel } from '../../../models/InputParametersHierarchy'
import { TemplateSelectRow } from '../../../components/InputParameterHierarchy/Template/TemplateSelectRow'

const AlertContainer = styled.div`
  display: flex;
  padding: 0.5rem;
  align-items: center;
  border-radius: 0.25rem;
  background: var(--zocks-gray-900, #212529);
  gap: 1rem;
  color: white;
`

export const MeetingTemplatePresetIconWrapper = styled.div`
  display: inline-flex;
  padding: 0.75rem;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.625rem;
  border-radius: 0.25rem;
  background: var(--zocks-primary);
  color: #fff;

  i {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
  }

  img {
    width: 2rem;
    height: 2rem;
  }
`

export interface MeetingTemplatePresetView {
  aiFeaturePresetId: string
  aiFeatureId?: string
  row: number
  column: number
  colSpan: number
}

export interface AISuggestionSwitcherProps {
  checked: boolean
  disabled: boolean
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

export const AISuggestionSwitcher: React.FC<AISuggestionSwitcherProps> = ({ checked, disabled, onChange }) => {
  return (
    <div className='d-flex align-items-center'>
      <Form.Check id='prefer-ai-calculation-checkbox' type='checkbox' disabled={disabled} className='d-flex'>
        <Form.Check.Input type='checkbox' disabled={disabled} checked={checked} onChange={onChange} className='me-2 zocks-border-color-primary' />
      </Form.Check>
      <i className={`bi bi-stars ${disabled ? 'zocks-text-gray-500' : 'zocks-text-primary'}`} />
      <span className={`zocks-px-0.25 ${disabled ? 'zocks-text-gray-500' : ''}`}>Prefer AI suggestion</span>
      <TagElement className={`lead-extra-small zocks-px-0.5 ${disabled ? 'zocks-text-gray-500' : 'zocks-text-primary zocks-bg-color-primary-light'}`}>
        new
      </TagElement>
    </div>
  )
}

const MEETING_TEMPLATE_SCOPE = {
  scope: 'meeting_template:update',
  superScope: ['meeting_template:admin'],
}

export const MeetingTemplateView: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)

  const { operation = 'details', meetingTemplateId = '', tabType = 'presets', modalType } = useParams()
  const navigate = useNavigate()
  const { tracked } = useTracking()
  const { hasScope } = useZocksAuth()
  const { hasQuota } = useZocksQuota()
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const { isAgendaEnabledForAccount, checkIsAgendaEnabledForMeetingTemplate } = useAgendaFeature()

  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const [presets, setPresets] = useState<MeetingTemplatePresetView[]>([])
  const [showAddPresetModal, setShowAddPresetModal] = useState<boolean>(false)
  const [showAddFormModal, setShowAddFormModal] = useState<boolean>(false)
  const [name, setName] = useState<string>('New Meeting Type')
  const [isAccountDefault, setIsAccountDefault] = useState<boolean>(false)
  const [isPrivate, setIsPrivate] = useState<boolean>(false)
  const [description, setDescription] = useState<string>('')
  const [metadata, setMetadata] = useState<string>('')
  const [averageMeetingTime, setAverageMeetingTime] = useState<string>('')
  const [meetingName, setMeetingName] = useState<string>('')
  const [meetingTemplateChanged, setMeetingTemplateChanged] = useState<boolean>(false)
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [showUpdateSystemModal, setShowUpdateSystemModal] = useState<boolean>(false)
  const [selectedPreset, setSelectedPreset] = useState<AiFeaturePreset | null>(null)
  const [showPresetConfigModal, setShowPresetConfigModal] = useState<boolean>(false)
  const [showMeetingAgendaConfigModal, setShowMeetingAgendaConfigModal] = useState<boolean>(false)
  const [showDefaultUserMeetingTemplateModal, setShowDefaultUserMeetingTemplateModal] = useState<boolean>(false)
  const [showMeetingScorecardConfigModal, setShowMeetingScorecardConfigModal] = useState<boolean>(false)
  const [showUpdateMeetingTemplatePrivacyModal, setShowUpdateMeetingTemplatePrivacyModal] = useState<boolean>(false)
  const [aiMeetingTemplateCalculatorEnabled, setAiMeetingTemplateCalculatorEnabled] = useState<boolean>(true)
  const [meetingScorecardEnabled, setMeetingScorecardEnabled] = useState<boolean>(true)
  const [isUserDefaultHovered, setIsUserIsHovered] = useState<boolean>(false)
  const [forms, setForms] = useState<AiFeature[]>([])
  const [createIsLoading, setCreateIsLoading] = useState<boolean>(false)
  const [meetingScorecardPreset, setMeetingScorecardPreset] = useState<AiFeaturePreset | null>(null)
  const hasIpHierarchyAdminScope = hasScope('**', 'iph:admin')

  const { data: formData } = useGetFeaturesQuery(
    { accountId, pageRequest: { page: 0, size: 100 }, type: 'scorecard', includeRestricted: true },
    { refetchOnMountOrArgChange: true },
  )

  const { data: presetData, isFetching: isFetchingPresetData } = useGetPresetsQuery(
    { accountId, pageRequest: { page: 0, size: 500 }, includeRestricted: true },
    { refetchOnMountOrArgChange: true },
  )

  const {
    data: userData,
    isLoading: isUserLoading,
    refetch: refetchUser,
  } = useGetUserByIdQuery({ accountId, userId: currentUserId }, { refetchOnMountOrArgChange: true })

  const [saveUserById, { isLoading: isUserUpdating }] = useSaveUserByIdMutation()
  const [getInputParameterHierarchyNode] = useLazyGetEffectiveNodeQuery()
  const [createInputParametersHierarchyNode] = useCreateNodeMutation()

  const [preferAISuggestion, setPreferAISuggestion] = useState<boolean>(true)

  const { updateMetadata: updateMeetingTemplateMetadata, isLoading: isUpdatingMeetingTemplateMetadata } = useUpdateMeetingTemplateMetadata(
    accountId,
    meetingTemplateId,
  )

  const meetingSummaryEnabled = hasQuota('ai.meetingSummaryAgent.value')
  const userLevelSettingsEnabled = hasQuota('ai.userLevelSettings.value')
  const formEnabled = hasQuota('ai.formFill.value')
  const { privateMeetingsEnabled } = usePrivateMeetingsEnabled()

  const formTabTitle = (
    <div className='d-flex align-items-center'>
      {!formEnabled ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
      Forms
    </div>
  )

  useEffect(() => {
    if (!account) {
      return
    }
    setAiMeetingTemplateCalculatorEnabled(account.data.config.aiMeetingTemplateCalculator)
    setMeetingScorecardEnabled(account.data.config.meetingScorecard)
  }, [account])

  const handleAiMeetingTemplateCalculatorOverrideChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!userData) {
      return
    }
    const currentUserData = userData.data
    setPreferAISuggestion(e.currentTarget.checked)
    saveUserById({
      accountId,
      userId: currentUserId,
      userData: {
        settings: {
          ...currentUserData.settings,
          aiMeetingTemplateCalculatorOverride: !e.currentTarget.checked,
        },
      },
    })
  }

  useEffect(() => {
    if (modalType === 'scorecard') {
      setShowMeetingScorecardConfigModal(true)
    }
  }, [modalType])

  useEffect(() => {
    if (!userData) {
      return
    }
    const currentUserData = userData.data
    setPreferAISuggestion(!currentUserData?.settings?.aiMeetingTemplateCalculatorOverride)
  }, [userData])

  const handleConfigureClicked = (preset: AiFeaturePreset) => {
    setSelectedPreset(preset)
    setShowPresetConfigModal(true)
  }

  const handleConfigModalHide = () => {
    setShowPresetConfigModal(false)
    setSelectedPreset(null)
  }

  const handleUpdateMeetingTemplatePrivacyModalHide = () => {
    setShowUpdateMeetingTemplatePrivacyModal(false)
  }

  const handleMeetingScorecardConfigModalHide = () => {
    navigate(`/workspace/setup/meeting-types/meeting-type/details/${meetingTemplateId}/presets`, { replace: true })
    setShowMeetingScorecardConfigModal(false)
  }

  const handleMeetingScorecardConfigModalShow = () => {
    setShowMeetingScorecardConfigModal(true)
    navigate(`/workspace/setup/meeting-types/meeting-type/details/${meetingTemplateId}/presets/scorecard`, { replace: true })
  }

  useEffect(() => {
    setMeetingScorecardPreset(getPresetById(MEETING_SCORE_PRESET_PRESET_ID) ?? null)
  }, [presetData])

  const [columns, setColumns] = useState<number>(2)
  const [rows, setRows] = useState<number>(1)

  const { data: meetingTemplateData, isFetching: isFetchingMeetingTemplateData } = useGetMeetingTemplateByIdQuery(
    { accountId, meetingTemplateId },
    { refetchOnMountOrArgChange: true, skip: !meetingTemplateId },
  )

  const templateSelectionSubHeader = meetingTemplateData ? `For ${meetingTemplateData.data.name} meeting type` : ''

  const { data: defaultMeetingTemplate } = useGetDefaultMeetingTemplateQuery({
    accountId: accountId,
    userId: authenticatedUser.id,
  })

  const { data: meetingAgendaIpHierarchyDefault } = useAgendaInputHierarchyDefaults(accountId, meetingTemplateData?.data)

  const [updateMeetingTemplate, { isLoading: updateIsLoading }] = useUpdateMeetingTemplateMutation()
  const [createMeetingTemplate] = useCreateMeetingTemplateMutation()
  const [setUserDefaultMeetingTemplate, { isLoading: setUserDefaultIsLoading }] = useSetUserDefaultMeetingTemplateMutation()

  const { getQuotaValue } = useZocksQuota()
  const maxPresetsInMeetingTemplate = getQuotaValue('ai.maxPresetsInMeetingTemplate.value') ?? 0
  const maxFormsInMeetingTemplate = getQuotaValue('ai.maxFormsInMeetingTemplate.value') ?? 0
  const activePresetCount = Object.values(presets).flat().length
  const hasUpdateRole = hasScope('**', 'meeting_template:update', ['meeting_template:admin'])

  const fromSystemMeetingTemplate = useMemo(() => {
    return !!meetingTemplateData?.data.fromSystemMeetingTemplate
  }, [meetingTemplateData])

  const isAgendaEnabledOnMeetingTemplate = useMemo(() => {
    return checkIsAgendaEnabledForMeetingTemplate(meetingTemplateData?.data)
  }, [meetingTemplateData, checkIsAgendaEnabledForMeetingTemplate])

  const copied = operation === 'copy'

  const shouldShowMeetingAgendaConfigModal = !!(showMeetingAgendaConfigModal && meetingAgendaIpHierarchyDefault)

  useEffect(() => {
    if (!meetingTemplateData?.data) {
      return
    }
    const {
      name,
      isDefault,
      isPrivate,
      detectionConfig: { description, averageMeetingTime, meetingName },
      presets: meetingTemplatePresets,
      formPresets: meetingTemplateForms,
      metadata,
    } = meetingTemplateData.data
    setName(copied ? `Copy of ${name}` : name)
    setIsAccountDefault(!copied && isDefault)
    setIsPrivate(isPrivate)
    setDescription(description)
    setAverageMeetingTime(averageMeetingTime ?? '')
    setMeetingName(meetingName ?? '')
    setMetadata(metadata ?? '')
    const presets = meetingTemplatePresets.map((preset, index) => {
      const { aiFeaturePresetId, aiFeatureId } = preset
      const { row = index, column = 'A', colSpan = 1 } = getMeetingTemplatePresetMetadata(preset)
      const columnIndex = MEETING_TEMPLATE_COLUMNS.indexOf(column)
      return {
        aiFeaturePresetId,
        aiFeatureId,
        row,
        column: columnIndex === -1 ? 0 : columnIndex,
        colSpan,
      }
    })
    setPresets(presets)

    if (formData && formData.data) {
      const formsInMeetingTemplate = formData.data.filter(({ id }) =>
        meetingTemplateForms.some((meetingTemplatePreset) => {
          return meetingTemplatePreset.aiFeatureId === id
        }),
      )
      setForms(formsInMeetingTemplate)
    }
  }, [formData, meetingTemplateData, copied])

  useEffect(() => {
    if (presets.length) {
      const maxRowIdx = Math.max(...presets.map(({ row }) => row))
      const maxColumnIdx = Math.max(...presets.map(({ column, colSpan }) => column + colSpan))
      setRows(maxRowIdx + 1)
      setColumns(maxColumnIdx)
    } else {
      setRows(1)
      setColumns(2)
    }
  }, [presets])

  useEffect(() => {
    if (!presets.length) {
      return
    }
    const emptyRow = Array.from(Array(rows).keys()).find((row) => !presets.some((p) => p.row === row))
    if (emptyRow !== undefined) {
      setPresets((presets) =>
        presets.map((preset) => {
          const { row } = preset
          return { ...preset, row: preset.row < emptyRow ? row : row - 1 }
        }),
      )
    }
  }, [presets, rows])

  const handleChangeMeetingTemplateName = (newName: string) => {
    setMeetingTemplateChanged(true)
    setName(newName)
  }

  const onDragEnd = useCallback(
    (result: DropResult) => {
      setIsDragging(false)
      const { draggableId, reason, destination } = result
      const draggedPreset = presets.find((preset) => preset.aiFeaturePresetId === draggableId)
      if (reason === 'CANCEL' || !destination || !draggedPreset) {
        return
      }

      setMeetingTemplateChanged(true)
      const { droppableId, index } = destination
      const [destColumn, row] = droppableId.split('-').map((s) => parseInt(s))
      const destRow = row + index
      const presetsInColumn = presets.filter((p) => p.column === destColumn)
      const spaceOccupied = presetsInColumn.some((p) => p.row === destRow)
      setPresets((presets) =>
        presets.map((preset) => {
          const { aiFeaturePresetId, column, row } = preset
          if (aiFeaturePresetId === draggableId) {
            return { ...preset, column: destColumn, row: destRow }
          }
          if (column !== destColumn) {
            return preset
          }
          return { ...preset, row: row + (spaceOccupied && row >= destRow ? 1 : 0) }
        }),
      )
    },
    [presets],
  )

  const onDragStart = useCallback(() => {
    setIsDragging(true)
  }, [])

  const getPresetDisplayName = (presetId: string) => {
    if (!presetData?.data) {
      return presetId
    }
    return presetData.data.find((p) => p.id === presetId)?.name ?? presetId
  }

  const getPresetIconUrl = (presetId: string) => {
    if (!presetData?.data) {
      return presetId
    }
    return presetData.data.find((p) => p.id === presetId)?.assetUrl
  }

  const getPresetById = (presetId: string) => {
    return presetData?.data.find((p) => p.id === presetId)
  }

  const handleAddMeetingTemplatePreset = () => {
    setShowAddPresetModal(true)
  }

  const handleAddForm = () => {
    setShowAddFormModal(true)
  }

  const handleMeetingTemplatePresetsAdded = (addedPresets: MeetingTemplatePreset[], removedPresetIds: string[]) => {
    setMeetingTemplateChanged(true)
    setPresets((presets) => [
      ...presets.filter((p) => !removedPresetIds.includes(p.aiFeaturePresetId)),
      ...addedPresets.map((preset, index) => {
        const { aiFeaturePresetId, aiFeatureId } = preset
        return {
          aiFeaturePresetId,
          aiFeatureId,
          row: rows + index,
          column: 0,
          colSpan: 1,
        }
      }),
    ])
    setShowAddPresetModal(false)
  }

  const handleMeetingTemplateFormAdded = (addedPresets: MeetingTemplatePreset[], removedFormIds: string[]) => {
    if (formData) {
      setMeetingTemplateChanged(true)
      setForms((forms) => [
        ...forms.filter((form) => !removedFormIds.includes(form.id)),
        ...formData.data.filter(({ id }) => addedPresets.some((meetingTemplatePreset) => meetingTemplatePreset.aiFeatureId === id)),
      ])

      setShowAddFormModal(false)
    }
  }

  const handleAddMeetingTemplatePresetCancelled = () => {
    setShowAddPresetModal(false)
  }

  const handleAddFormCancelled = () => {
    setShowAddFormModal(false)
  }

  const handleDeleteMeetingTemplatePreset = (presetId: string) => {
    setMeetingTemplateChanged(true)
    setPresets((presets) => presets.filter((p) => p.aiFeaturePresetId !== presetId))
  }

  const handleDeleteMeetingTemplateForm = (formId: string) => {
    setMeetingTemplateChanged(true)
    setForms((forms) => forms.filter((form) => form.id !== formId))
  }

  const handleMakeDefault = () => {
    updateMeetingTemplate({ accountId, meetingTemplateId, payload: { isDefault: true } })
  }

  const handleChangeTemplatePrivacy = (changePastMeetings: boolean) => {
    updateMeetingTemplate({ accountId, meetingTemplateId, payload: { isPrivate: !isPrivate, changePastMeetings } })
    setShowUpdateMeetingTemplatePrivacyModal(false)
  }

  const handleShowDefaultUserMeetingTemplateModal = () => {
    if (aiMeetingTemplateCalculatorEnabled) {
      setShowDefaultUserMeetingTemplateModal(true)
    } else {
      setUserDefaultMeetingTemplate({ accountId, payload: { meetingTemplateId: meetingTemplateId } })
    }
  }

  const handleMakeUserDefault = () => {
    setShowDefaultUserMeetingTemplateModal(false)
    setUserDefaultMeetingTemplate({ accountId, payload: { meetingTemplateId: meetingTemplateId } })
    refetchUser()
  }

  const handleRemoveUserDefault = () => {
    if (!userData) {
      return
    }
    setUserDefaultMeetingTemplate({ accountId, payload: { meetingTemplateId: null } })
    const currentUserData = userData.data
    saveUserById({
      accountId,
      userId: currentUserId,
      userData: { settings: { ...currentUserData.settings, aiMeetingTemplateCalculatorOverride: null } },
      keepEmptyItems: true,
    })
  }

  const handleSetDescription = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMeetingTemplateChanged(true)
    setDescription(e.currentTarget.value)
  }

  const handleSetAverageMeetingTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMeetingTemplateChanged(true)
    setAverageMeetingTime(e.currentTarget.value)
  }

  const handleSetMeetingName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMeetingTemplateChanged(true)
    setMeetingName(e.currentTarget.value)
  }

  const _handleToggleAgenda = async () => {
    const newValue = !isAgendaEnabledOnMeetingTemplate
    await updateMeetingTemplateMetadata({ isAgendaEnabled: newValue })
  }

  const handleToggleAgenda = tracked(_handleToggleAgenda, {
    eventName: TrackEvent.AGENDA_TOGGLE_CLICKED,
    eventDetails: {
      isAgendaEnabled: !isAgendaEnabledOnMeetingTemplate,
      meetingTemplateId,
    },
  })

  const handleCancel = () => {
    navigate('/workspace/setup/meeting-types')
  }

  const handleSaveButtonClicked = async () => {
    if (fromSystemMeetingTemplate && !copied) {
      setShowUpdateSystemModal(true)
    } else {
      await handleSaveMeetingTemplate()
    }
  }

  const [selectedTab, setSelectedTab] = useState(tabType)

  const changeTab = (key: string | null) => {
    if (key) {
      setSelectedTab(key)
      if (meetingTemplateId === '') {
        navigate(`/workspace/setup/meeting-types/meeting-type/create/${key}`, { replace: true })
      } else {
        navigate(`/workspace/setup/meeting-types/meeting-type/details/${meetingTemplateId}/${key}`, { replace: true })
      }
    }
  }

  const getPayloadForSavingMeetingTemplate = (newMeetingTemplateName?: string) => {
    const presetsWithMetadata = presets.map((preset) => {
      const { aiFeaturePresetId, aiFeatureId, row, column, colSpan } = preset
      return {
        aiFeaturePresetId,
        aiFeatureId,
        metadata: JSON.stringify({ row, column: MEETING_TEMPLATE_COLUMNS[column], colSpan }),
      }
    })
    const formsToAdd = forms
      .filter((form) => form.preset)
      .map((form) => {
        const { preset, id } = form
        return {
          aiFeaturePresetId: preset!.id,
          aiFeatureId: id,
          metadata: '',
        }
      })

    return {
      name: newMeetingTemplateName ?? name,
      isDefault: isAccountDefault,
      isPrivate,
      detectionConfig: {
        description,
        averageMeetingTime,
        meetingName,
      },
      presets: [...presetsWithMetadata],
      formPresets: [...formsToAdd],
      metadata,
    }
  }

  const _handleSaveMeetingTemplate = async (newMeetingTemplateName?: string) => {
    setMeetingTemplateChanged(false)
    const payload = getPayloadForSavingMeetingTemplate(newMeetingTemplateName)
    if (meetingTemplateId && !fromSystemMeetingTemplate && !copied) {
      try {
        await updateMeetingTemplate({
          accountId,
          meetingTemplateId,
          payload,
        }).unwrap()
        navigate('/workspace/setup/meeting-types')
      } catch (e) {
        console.error(e)
      }
    } else {
      setCreateIsLoading(true)
      try {
        const newMeetingTemplate = await createMeetingTemplate({ accountId, payload }).unwrap()
        if (copied) {
          await copyTemplates(newMeetingTemplate.id)
        }
        navigate('/workspace/setup/meeting-types')
      } catch (e) {
        console.error(e)
      } finally {
        setCreateIsLoading(false)
      }
    }
  }

  const handleSaveMeetingTemplate = tracked(
    _handleSaveMeetingTemplate,
    {
      eventName: meetingTemplateId
        ? fromSystemMeetingTemplate
          ? TrackEvent.MEETING_TEMPLATE_CREATED_FROM_SYSTEM
          : copied
            ? TrackEvent.MEETING_TEMPLATE_COPIED
            : TrackEvent.MEETING_TEMPLATE_UPDATED
        : TrackEvent.MEETING_TEMPLATE_CREATED,
      eventDetails: getPayloadForSavingMeetingTemplate(),
    },
    {
      eventDetailsExtractor: (newMeetingTemplateName?: string) => {
        return newMeetingTemplateName ? { name: newMeetingTemplateName } : {}
      },
    },
  )

  const copyTemplates = async (newMeetingTemplateId: string) => {
    const templatesToCopy = [
      { type: InputParameterHierarchyType.MEETING_SUMMARY_TEMPLATE, typeId: 'summary-template' },
      { type: InputParameterHierarchyType.MEETING_PREPARATION_TEMPLATE, typeId: 'prep-template' },
      { type: InputParameterHierarchyType.MEETING_PREPARATION_EMAIL_TEMPLATE, typeId: 'prep-template' },
      { type: InputParameterHierarchyType.MEETING_AGENDA_CONFIG, typeId: 'agenda-conf' },
    ]
    await Promise.all(templatesToCopy.map(({ type, typeId }) => copyTemplate(type, typeId, newMeetingTemplateId)))
  }

  const copyTemplate = async (type: InputParameterHierarchyType, typeId: string, newMeetingTemplateId: string) => {
    const template = await getInputParameterHierarchyNode({
      accountId,
      type,
      typeId,
      context: { effective: false, level: InputParametersHierarchyLevel.MEETING_TEMPLATE_GLOBAL, meetingTemplateId },
    }).unwrap()
    if (!template) {
      return
    }

    const hierarchyId: InputParametersHierarchyId = {
      level: InputParametersHierarchyLevel.MEETING_TEMPLATE_GLOBAL,
      meetingTemplateId: newMeetingTemplateId,
    }

    const node: CreateInputParametersHierarchyNode = template.templateId
      ? {
          hierarchyId,
          templateId: template.templateId,
        }
      : {
          hierarchyId,
          inputParameters: template.inputParameters,
        }

    try {
      await createInputParametersHierarchyNode({
        accountId,
        type,
        typeId,
        node,
      }).unwrap()
    } catch (e) {
      console.error(e)
    }
  }

  const handleAddColumn = () => {
    if (columns === MEETING_TEMPLATE_COLUMNS.length) {
      return
    }
    setColumns((columns) => columns + 1)
  }

  const handleRemoveColumn = () => {
    if (columns === 1) {
      return
    }
    setColumns((columns) => columns - 1)
    setPresets((presets) => presets.filter((p) => p.column !== columns - 1 && p.column + p.colSpan <= columns - 1))
  }

  const meetingTemplatePresetExpandable = (presetId: string) => {
    const preset = presets.find((p) => p.aiFeaturePresetId === presetId)
    if (!preset) {
      return
    }
    const { row, column, colSpan } = preset
    if (column + colSpan === columns) {
      return false
    }

    return !presets.some((p) => p.row === row && p.column === column + 1)
  }

  const handleExpandMeetingTemplatePreset = (presetId: string) => {
    setMeetingTemplateChanged(true)
    setPresets((presets) =>
      presets.map((p) => {
        const { aiFeaturePresetId, colSpan } = p
        if (aiFeaturePresetId !== presetId) {
          return p
        }

        return {
          ...p,
          colSpan: colSpan + 1,
        }
      }),
    )
  }

  const meetingTemplatePresetCollapsible = (presetId: string) => {
    const preset = presets.find((p) => p.aiFeaturePresetId === presetId)
    if (!preset) {
      return
    }
    const { colSpan } = preset
    return colSpan > 1
  }

  const handleCollapseMeetingTemplatePreset = (presetId: string) => {
    setMeetingTemplateChanged(true)
    setPresets((presets) =>
      presets.map((p) => {
        const { aiFeaturePresetId, colSpan } = p
        if (aiFeaturePresetId !== presetId) {
          return p
        }

        return {
          ...p,
          colSpan: colSpan - 1,
        }
      }),
    )
  }

  const currentPresetIds = useMemo(() => {
    return Object.values(presets)
      .flat()
      .map((p) => p.aiFeaturePresetId)
  }, [presets])

  const isLoading = isFetchingMeetingTemplateData || isFetchingPresetData || createIsLoading || updateIsLoading

  const handleNavigateForm = (form: AiFeature) => {
    navigate(`/workspace/setup/forms/form/details/${form.id}`)
  }
  const FormListItem: React.FC<{
    form: AiFeature
  }> = ({ form }) => {
    const accountId = useAppSelector(selectAccountId)
    const { data: scorecardData } = useGetAllScorecardsQuery({
      accountId: accountId,
      aiFeatureIds: form.id,
    })

    const formName = scorecardData?.data.find((scorecard) => scorecard.aiFeatureIds[0] === form.id)?.name

    return formName ? (
      <Container>
        <Row className='m-2 py-3 px-2 meetingTemplatePreset align-items-center cursor-pointer'>
          <Col className='d-flex gap-2 align-items-center justify-content-between' onClick={() => handleNavigateForm(form)}>
            <div
              className={classNames('text-truncate', {
                'lead-medium': columns <= 2,
                'lead-small': columns > 2,
              })}
            >
              {formName}
            </div>

            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={MEETING_TEMPLATE_SCOPE}>
              <Button
                className='px-2 py-2'
                variant='primaryGhost'
                onClick={(event) => {
                  event.stopPropagation()
                  event.preventDefault()
                  handleDeleteMeetingTemplateForm(form.id)
                }}
              >
                <i className='bi bi-trash' />
              </Button>
            </ScopedBlock>
          </Col>
        </Row>
      </Container>
    ) : (
      <LoadingResult></LoadingResult>
    )
  }

  return (
    <>
      {showMeetingScorecardConfigModal && meetingScorecardPreset && (
        <MeetingTemplatePresetFormConfigModal
          show={showMeetingScorecardConfigModal}
          onHide={handleMeetingScorecardConfigModalHide}
          preset={meetingScorecardPreset}
          meetingTemplateId={meetingTemplateId}
        />
      )}
      {showAddPresetModal && (
        <AddMeetingTemplatePresetModal
          show={showAddPresetModal}
          onSave={handleMeetingTemplatePresetsAdded}
          onCancel={handleAddMeetingTemplatePresetCancelled}
          currentPresetIds={currentPresetIds}
        />
      )}
      {showAddFormModal && (
        <AddMeetingTemplateFormModal show={showAddFormModal} onSave={handleMeetingTemplateFormAdded} onCancel={handleAddFormCancelled} currentForms={forms} />
      )}
      {showDefaultUserMeetingTemplateModal && (
        <DefaultUserMeetingTemplateModal
          show={showDefaultUserMeetingTemplateModal}
          onHide={handleMakeUserDefault}
          defaultMeetingTemplate={meetingTemplateData?.data}
        />
      )}
      <MeetingAgendaInputParametersHierarchyConfigModal
        show={shouldShowMeetingAgendaConfigModal}
        onHide={() => setShowMeetingAgendaConfigModal(false)}
        meetingTemplateId={meetingTemplateId}
        defaultInputParameters={meetingAgendaIpHierarchyDefault?.inputParameters ?? null}
      />
      {showPresetConfigModal && selectedPreset ? (
        <MeetingTemplatePresetFormConfigModal
          show={showPresetConfigModal}
          onHide={handleConfigModalHide}
          preset={selectedPreset}
          meetingTemplateId={meetingTemplateId}
        />
      ) : null}
      {showUpdateMeetingTemplatePrivacyModal && (
        <UpdateMeetingTemplatePrivacyModal
          show={showUpdateMeetingTemplatePrivacyModal}
          onConfirm={handleChangeTemplatePrivacy}
          onCancel={handleUpdateMeetingTemplatePrivacyModalHide}
          isPrivate={!isPrivate}
        />
      )}
      <Container>
        {isLoading && (
          <div className='d-flex align-items-center justify-content-center'>
            <Spinner />
          </div>
        )}
        {showUpdateSystemModal ? (
          <UpdateSystemMeetingTemplateModal
            show={showUpdateSystemModal}
            onCancel={() => setShowUpdateSystemModal(false)}
            onConfirm={handleSaveMeetingTemplate}
            meetingTemplateName={name}
          />
        ) : null}
        {!isLoading && (!meetingTemplateId || meetingTemplateData?.data) && (
          <>
            <Row>
              <Col className='p-0'>
                <Breadcrumb>
                  <Breadcrumb.Item
                    linkProps={{
                      to: '/workspace/setup/meeting-types',
                      className: `${buttonStyles.button} ${buttonStyles.linkButton} text-decoration-underline`,
                    }}
                    linkAs={NavLink}
                  >
                    Meeting Types
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active>{name}</Breadcrumb.Item>
                </Breadcrumb>
              </Col>
            </Row>
            {meetingTemplateId ? (
              <Row>
                <Col className='p-0 d-flex gap-2 mt-2 mb-3'>
                  {isAccountDefault ? <TagElement>Account Default</TagElement> : null}
                  {fromSystemMeetingTemplate ? <TagElement>System</TagElement> : null}
                  {defaultMeetingTemplate?.isUserDefault && defaultMeetingTemplate?.id === meetingTemplateId ? <TagElement>My Default</TagElement> : null}
                </Col>
              </Row>
            ) : null}
            <Row className='d-flex align-items-center gy-3'>
              <Col className='d-flex gap-2 p-0 m-0 mt-2'>
                <div className='d-flex flex-column flex-grow-1'>
                  <label htmlFor='meeting-template-name' className='label-medium'>
                    Meeting type name
                  </label>
                  <Form.Control
                    id='meeting-template-name'
                    type='text'
                    value={name}
                    onChange={(e) => handleChangeMeetingTemplateName(e.currentTarget.value)}
                    placeholder='Meeting type name'
                  />
                </div>
              </Col>
            </Row>
            <Row className='zocks-mt-3.5 zocks-mb-3'>
              <Col className='p-0 d-flex flex-column gap-3'>
                <h4>Default settings</h4>
                <div className='d-flex flex-column'>
                  <div className='d-flex zocks-pb-1.25 gap-2 justify-content-between align-items-center meetingTemplateDefaultSettingsContainer'>
                    <div className='d-flex align-items-center gap-2'>
                      <i className='bi bi-people-fill fs-4' />
                      {!copied && isAccountDefault ? (
                        <div className='d-flex align-items-center gap-1'>
                          <div>
                            This meeting type is the <span className='bold zocks-text-primary'>account default</span>
                          </div>
                          <i className='bi bi-check2 fs-5 zocks-text-primary' />
                        </div>
                      ) : (
                        <div>
                          Set this meeting type as <span className='bold'>account default</span>
                        </div>
                      )}
                    </div>
                    {!isAccountDefault && (
                      <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={MEETING_TEMPLATE_SCOPE}>
                        <Button
                          className='button-medium'
                          variant='primary'
                          onClick={handleMakeDefault}
                          disabled={copied || isAccountDefault || !meetingTemplateId}
                        >
                          Make Account Default
                        </Button>
                      </ScopedBlock>
                    )}
                  </div>
                  <div
                    className='d-flex zocks-pt-1.25 gap-2 justify-content-between align-items-center meetingTemplateDefaultSettingsContainer'
                    onMouseEnter={() => setIsUserIsHovered(true)}
                    onMouseLeave={() => setIsUserIsHovered(false)}
                  >
                    <div className='d-flex align-items-center gap-2'>
                      <i className='bi bi-person-fill fs-4' />
                      {!copied && defaultMeetingTemplate?.isUserDefault && defaultMeetingTemplate?.id === meetingTemplateId ? (
                        <div className='d-flex align-items-center gap-1'>
                          <div>
                            This meeting type is set as <span className='bold zocks-text-primary'>my default</span>
                          </div>
                          <i className='bi bi-check2 fs-5 zocks-text-primary' />
                        </div>
                      ) : (
                        <div>
                          Set this meeting type as <span className='bold'>my default</span>
                        </div>
                      )}
                    </div>
                    {userLevelSettingsEnabled ? (
                      <ScopedBlock
                        missingScopeAction={MissingScopeAction.DISABLE}
                        scope={{
                          scope: 'meeting_template:set_user_default',
                          superScope: ['meeting_template:admin'],
                        }}
                        quota={{ quota: 'ai.userLevelSettings.value' }}
                      >
                        {!copied && defaultMeetingTemplate?.isUserDefault && defaultMeetingTemplate?.id === meetingTemplateId ? (
                          <div className='d-flex justify-content-end'>
                            <Row className='d-flex align-items-center'>
                              {aiMeetingTemplateCalculatorEnabled ? (
                                <Col xs='auto' className='d-flex align-items-center py-2'>
                                  <AISuggestionSwitcher
                                    checked={preferAISuggestion}
                                    disabled={isUserLoading || isAccountLoading || isUserUpdating}
                                    onChange={handleAiMeetingTemplateCalculatorOverrideChanged}
                                  />
                                  <AIInfoTooltip />
                                </Col>
                              ) : null}
                              {isUserDefaultHovered || !aiMeetingTemplateCalculatorEnabled ? (
                                <Col xs='auto' className='d-flex justify-content-end'>
                                  <Button className='button-medium' variant='dangerGhost' onClick={handleRemoveUserDefault} loading={setUserDefaultIsLoading}>
                                    <i className='bi bi-trash' />
                                    Remove my default
                                  </Button>
                                </Col>
                              ) : null}
                            </Row>
                          </div>
                        ) : (
                          <div className='d-flex justify-content-end'>
                            <Row className='d-flex align-items-center'>
                              {aiMeetingTemplateCalculatorEnabled ? (
                                <Col xs='auto' className='d-flex align-items-center'>
                                  <AISuggestionSwitcher
                                    checked={preferAISuggestion}
                                    disabled={true}
                                    onChange={handleAiMeetingTemplateCalculatorOverrideChanged}
                                  />
                                  <AIInfoTooltip />
                                </Col>
                              ) : null}
                              <Col xs='auto' className='d-flex justify-content-end'>
                                <Button
                                  className='button-medium'
                                  variant='primary'
                                  onClick={handleShowDefaultUserMeetingTemplateModal}
                                  loading={setUserDefaultIsLoading}
                                  disabled={copied || !meetingTemplateId}
                                >
                                  Make my default
                                </Button>
                              </Col>
                            </Row>
                          </div>
                        )}
                      </ScopedBlock>
                    ) : (
                      <ScopedBlock
                        missingScopeAction={MissingScopeAction.DISABLE}
                        scope={{
                          scope: 'account:update',
                          superScope: ['account:admin'],
                        }}
                      >
                        <Button
                          className='d-flex p-2 align-items-center text-nowrap button-medium'
                          variant='primary'
                          onClick={() => {
                            navigate('/account/plan')
                          }}
                        >
                          <img alt='Upgrade' src={PrizeBadge} />
                          Upgrade plan
                        </Button>
                      </ScopedBlock>
                    )}
                  </div>
                  {privateMeetingsEnabled ? (
                    <div className='d-flex zocks-pb-1.25 gap-2 justify-content-between align-items-center meetingTemplateDefaultSettingsContainer'>
                      <div className='d-flex align-items-center gap-2'>
                        {isPrivate ? (
                          <>
                            <i className='bi bi-lock fs-4' />
                            <Stack gap={1}>
                              <p className='paragraph-medium mb-0'>
                                Meetings are <span className='bold'>private</span> (visible to participants only) by default
                              </p>
                              <p className='paragraph-small mb-0'>Exceptions: manual changes to public</p>
                            </Stack>
                          </>
                        ) : (
                          <>
                            <i className='bi bi-unlock fs-4' />
                            <Stack gap={1}>
                              <p className='paragraph-medium mb-0'>
                                Meetings are <span className='bold'>public</span> (visible to everyone) by default
                              </p>
                              <p className='paragraph-small mb-0'>Exceptions: private calendar events, manual changes to private</p>
                            </Stack>
                          </>
                        )}
                      </div>
                      <MultiScopedBlock
                        scopedBlocks={[
                          {
                            missingScopeAction: MissingScopeAction.DISABLE,
                            scope: {
                              scope: 'meeting_template:update',
                              superScope: ['meeting_template:admin'],
                            },
                          },
                          {
                            missingScopeAction: MissingScopeAction.DISABLE,
                            scope: { scope: 'account:compliance_config_update', superScope: [] },
                          },
                        ]}
                      >
                        <Button
                          className='button-medium'
                          variant='primary'
                          onClick={() => setShowUpdateMeetingTemplatePrivacyModal(true)}
                          disabled={fromSystemMeetingTemplate || !meetingTemplateId}
                        >
                          {isPrivate ? 'Set default to public' : 'Set default to private'}
                        </Button>
                      </MultiScopedBlock>
                    </div>
                  ) : null}
                  {isAgendaEnabledForAccount ? (
                    <div className='meetingTemplateDefaultSettingsContainer'>
                      <div className='d-flex justify-content-between align-items-center w-100'>
                        <div className='d-flex align-items-center gap-2'>
                          <i className='bi bi-list-ul fs-4' />
                          <div>
                            Agenda generation is <span className='bold'>turned {isAgendaEnabledOnMeetingTemplate ? 'on' : 'off'}</span>
                          </div>
                        </div>

                        <div className='d-flex justify-content-end gap-3 align-items-center'>
                          <Button
                            className='button-small'
                            variant='grayGhost'
                            onClick={() => setShowMeetingAgendaConfigModal(true)}
                            disabled={copied || !meetingTemplateId || !isAgendaEnabledOnMeetingTemplate}
                          >
                            Customize
                          </Button>

                          <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={MEETING_TEMPLATE_SCOPE}>
                            <Button
                              className='d-flex p-2 align-items-center text-nowrap button-medium'
                              variant='primary'
                              onClick={handleToggleAgenda}
                              loading={isUpdatingMeetingTemplateMetadata}
                              disabled={copied || !meetingTemplateId || fromSystemMeetingTemplate}
                            >
                              {isAgendaEnabledOnMeetingTemplate ? 'Disable' : 'Enable'} Agenda
                            </Button>
                          </ScopedBlock>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className='meetingTemplateDefaultSettingsContainer'>
                      <div className='d-flex justify-content-between align-items-center w-100'>
                        <div className='d-flex align-items-center gap-2'>
                          <i className='bi bi-list-ul fs-4' />
                          <div>
                            Agenda generation is <span className='bold'>turned off</span>
                          </div>
                        </div>

                        <ScopedBlock
                          missingScopeAction={MissingScopeAction.DISABLE}
                          scope={{
                            scope: 'account:update',
                            superScope: ['account:admin'],
                          }}
                        >
                          <Button
                            className='d-flex p-2 align-items-center text-nowrap'
                            variant='primary'
                            onClick={() => {
                              navigate('/account/plan')
                            }}
                          >
                            <img alt='Upgrade' src={PrizeBadge} />
                            Upgrade plan
                          </Button>
                        </ScopedBlock>
                      </div>
                    </div>
                  )}
                  <div className='meetingTemplateDefaultSettingsContainer'>
                    <div className='d-flex justify-content-between align-items-center w-100'>
                      <div className='d-flex align-items-center gap-2'>
                        <i className='bi bi-speedometer2 fs-4' />
                        <div>Meeting scorecard</div>
                      </div>

                      <div className='d-flex justify-content-end gap-3 align-items-center'>
                        <Button
                          className='button-small'
                          variant='grayGhost'
                          onClick={handleMeetingScorecardConfigModalShow}
                          disabled={copied || !meetingTemplateId || !meetingScorecardEnabled}
                        >
                          Customize
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
            <Row className='zocks-mt-3.5 zocks-mb-3'>
              <Col className='p-0'>
                <h4 className='mb-1'>Templates</h4>
                <div className='paragraph-medium'>Choose which templates to use for this meeting type. Your personal choice overrides the account default.</div>
                <Table className={`mt-4 templatesTable`}>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Your choice</th>
                      <th>Meeting Type default</th>
                      {hasIpHierarchyAdminScope ? <th>User-specific</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {meetingSummaryEnabled ? (
                      <TemplateSelectRow
                        templateSelectionSubHeader={templateSelectionSubHeader}
                        type={InputParameterHierarchyType.MEETING_SUMMARY_TEMPLATE}
                        typeId='summary-template'
                        context={{
                          meetingTemplateId,
                          userId: currentUserId,
                          defaultTypeId: meetingTemplateData?.data.systemMeetingTemplateId,
                        }}
                      />
                    ) : null}
                    <TemplateSelectRow
                      templateSelectionSubHeader={templateSelectionSubHeader}
                      type={InputParameterHierarchyType.MEETING_PREPARATION_TEMPLATE}
                      typeId='prep-template'
                      context={{
                        meetingTemplateId,
                        userId: currentUserId,
                        defaultTypeId: meetingTemplateData?.data.systemMeetingTemplateId,
                      }}
                    />
                    <TemplateSelectRow
                      templateSelectionSubHeader={templateSelectionSubHeader}
                      type={InputParameterHierarchyType.MEETING_PREPARATION_EMAIL_TEMPLATE}
                      typeId='prep-template'
                      context={{
                        meetingTemplateId,
                        userId: currentUserId,
                        defaultTypeId: meetingTemplateData?.data.systemMeetingTemplateId,
                      }}
                    />
                    {presets
                      .map(({ aiFeaturePresetId }) => getPresetById(aiFeaturePresetId))
                      .filter((preset) => Boolean(preset?.inputParametersSchema?.properties?.template))
                      .map((preset) => {
                        return (
                          <TemplateSelectRow
                            title={
                              <>
                                {preset!.name} <span className='zocks-text-gray-600 label-small'>(Preset)</span>
                                <OverlayTrigger
                                  placement='top'
                                  overlay={
                                    <BootstrapTooltip>This template is included with the ‘{preset!.name}’ preset added to this meeting type.</BootstrapTooltip>
                                  }
                                >
                                  <i className='bi bi-info-square zocks-text-gray-600 ps-1 label-large' />
                                </OverlayTrigger>
                              </>
                            }
                            templateSelectionSubHeader={templateSelectionSubHeader}
                            type={InputParameterHierarchyType.AI_FEATURE_PRESET}
                            typeId={preset!.id}
                            context={{
                              meetingTemplateId,
                              userId: currentUserId,
                              defaultTypeId: preset!.id,
                            }}
                          />
                        )
                      })}
                  </tbody>
                </Table>
              </Col>
            </Row>

            <Row>
              <Col className='p-0 mb-2'>
                <h4>Configuration</h4>
              </Col>
            </Row>
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={MEETING_TEMPLATE_SCOPE}>
              <Row>
                <Col className='p-0 mb-2'>
                  <div className='d-flex gap-2'>
                    <div className='paragraph-medium'>Description</div>
                    <Tooltip className='zocks-text-gray-500' placement='right' title='A short description of the meetings described by this meeting type.'>
                      <i className='bi bi-info-circle' />
                    </Tooltip>
                  </div>
                  <div className='flex-grow-1 d-flex zocks-form-container'>
                    <Form.Control as='textarea' rows={2} value={description} onChange={handleSetDescription} placeholder={'Meeting type description'} />
                  </div>
                </Col>
              </Row>
              <Row className='mb-2 gap-3'>
                <Col className='p-0'>
                  <div className='d-flex gap-2'>
                    <div className='paragraph-medium'>Average meeting time</div>
                    <Tooltip
                      className='zocks-text-gray-500'
                      placement='right'
                      title='Average meeting time for this meeting type. For example "15 minutes" or "1 hour".'
                    >
                      <i className='bi bi-info-circle' />
                    </Tooltip>
                  </div>
                  <Form.Control type='text' value={averageMeetingTime} onChange={handleSetAverageMeetingTime} placeholder='15 minutes, 1 hour, etc.' />
                </Col>
                <Col className='p-0'>
                  <div className='d-flex gap-2'>
                    <div className='paragraph-medium'>Typical meeting name</div>
                    <Tooltip className='zocks-text-gray-500' placement='right' title='Common keywords in the meeting name, such as "Introduction" or "Review".'>
                      <i className='bi bi-info-circle' />
                    </Tooltip>
                  </div>
                  <Form.Control type='text' value={meetingName} onChange={handleSetMeetingName} placeholder='Review, introduction, etc.' />
                </Col>
              </Row>
            </ScopedBlock>
            <Row>
              <Col>
                <Tabs onSelect={changeTab} defaultActiveKey={selectedTab} id='config-tabs' className='d-flex gap-2r mb-3'>
                  <Tab className='mt-5' eventKey='presets' title='Presets'>
                    <Row className='mb-2 zocks-mt-3'>
                      <Col className='p-0 d-flex gap-2 align-items-center'>
                        <h4>Presets {`(${activePresetCount}/${maxPresetsInMeetingTemplate})`}</h4>
                        <Tooltip
                          className='zocks-text-gray-500'
                          placement='right'
                          title='Drag-and-drop presets to rearrange them. Only single column presets can be dragged.'
                        >
                          <i className='bi bi-info-circle' />
                        </Tooltip>
                      </Col>
                    </Row>
                    {meetingTemplateId && (
                      <Row className='p-0 d-flex align-items-center'>
                        <Col className='p-0 my-3'>
                          <AlertContainer>
                            <i className='bi bi-exclamation-circle-fill' />
                            <div className='paragraph-medium'>
                              Adding new presets won't recalculate your existing meetings. You will be able to manually run the newly added presets on the
                              existing meetings.
                            </div>
                          </AlertContainer>
                        </Col>
                      </Row>
                    )}
                    {activePresetCount ? (
                      <Row className='my-2'>
                        <Col className='d-flex gap-2 align-items-center justify-content-end'>
                          <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={MEETING_TEMPLATE_SCOPE}>
                            <Button
                              className='button-small'
                              variant='primaryGhost'
                              onClick={handleAddColumn}
                              disabled={columns === MEETING_TEMPLATE_COLUMNS.length}
                            >
                              Add column
                            </Button>
                            <Button className='button-small' variant='primaryGhost' onClick={handleRemoveColumn} disabled={columns === 1}>
                              Remove column
                            </Button>
                          </ScopedBlock>
                        </Col>
                      </Row>
                    ) : null}
                    <DragDropContext onDragEnd={onDragEnd} onBeforeDragStart={onDragStart}>
                      <Row className='d-flex gap-3 zocks-mb-4'>
                        {!activePresetCount ? (
                          <Col className='p-0'>
                            <div className='paragraph-medium'>No presets yet.</div>
                          </Col>
                        ) : null}
                        <Col className='p-0'>
                          {Array.from(Array(rows).keys()).map((row) => {
                            return (
                              <Container key={row}>
                                <Row>
                                  {Array.from(Array(columns).keys()).map((column) => {
                                    const droppableId = `${column}-${row}`
                                    const preset = presets.find(({ row: presetRow, column: presetColumn }) => {
                                      return presetRow === row && presetColumn === column
                                    })
                                    if (!preset) {
                                      const needsEmptySpace = presets
                                        .filter(({ row: presetRow, colSpan }) => presetRow === row && colSpan !== 1)
                                        .map(({ column: presetColumn, colSpan }) => {
                                          return Array.from(Array(columns).keys()).slice(presetColumn, presetColumn + colSpan)
                                        })
                                        .every((presetColumns) => !presetColumns.includes(column))
                                      if (!needsEmptySpace) {
                                        return null
                                      }
                                      const colStyle = `col-${12 / columns}`
                                      return (
                                        <Droppable droppableId={droppableId}>
                                          {(provided) => (
                                            <Col
                                              {...provided.droppableProps}
                                              ref={provided.innerRef}
                                              className={classNames(colStyle, {
                                                draggingOver: isDragging,
                                              })}
                                            >
                                              {provided.placeholder}
                                            </Col>
                                          )}
                                        </Droppable>
                                      )
                                    }
                                    const { aiFeaturePresetId, colSpan } = preset
                                    const aiFeaturePreset = getPresetById(aiFeaturePresetId)
                                    const colStyle = `col-${(colSpan * 12) / columns}`
                                    const isDragDisabled = colSpan !== 1 || !hasUpdateRole
                                    return (
                                      <Droppable droppableId={droppableId}>
                                        {(provided, snapshot) => (
                                          <Col
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                            className={classNames({
                                              draggingOver: isDragging,
                                              [colStyle]: !snapshot.draggingFromThisWith,
                                            })}
                                          >
                                            <Draggable key={aiFeaturePresetId} draggableId={aiFeaturePresetId} index={0} isDragDisabled={isDragDisabled}>
                                              {(provided) => (
                                                <Container>
                                                  <Row
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    className={classNames('meetingTemplatePresetContainer p-0 my-2', { dragDisabled: isDragDisabled })}
                                                  >
                                                    <Col>
                                                      <Container className='p-2'>
                                                        <Row className='zocks-p-1.25 meetingTemplatePreset align-items-center'>
                                                          <Col className='p-0 d-flex gap-2 align-items-center justify-content-between'>
                                                            <div className='d-flex gap-3 align-items-start'>
                                                              <div className='meeting-template-preset-grip-wrapper d-flex align-items-center justify-content-center'>
                                                                <i className='bi bi-grip-vertical' />
                                                              </div>
                                                              <div className='d-flex align-items-start gap-3'>
                                                                <MeetingTemplatePresetIconWrapper>
                                                                  <img src={getPresetIconUrl(aiFeaturePresetId)} alt='asset logo' />
                                                                </MeetingTemplatePresetIconWrapper>
                                                                <div className='d-flex flex-column justify-content-center meeting-template-preset-name-container'>
                                                                  <span
                                                                    className={classNames('text-truncate', {
                                                                      'lead-medium': columns <= 2,
                                                                      'lead-small': columns > 2,
                                                                    })}
                                                                  >
                                                                    {getPresetDisplayName(aiFeaturePresetId)}
                                                                  </span>
                                                                  {aiFeaturePreset ? <AIFeaturePresetBusinessEvents aiFeaturePreset={aiFeaturePreset} /> : null}
                                                                </div>
                                                              </div>
                                                            </div>
                                                            <div className='d-flex gap-2 align-items-center'>
                                                              {aiFeaturePreset?.inputParametersSchema?.properties &&
                                                              !aiFeaturePreset.inputParametersSchema.properties.template ? (
                                                                <ScopedBlock
                                                                  missingScopeAction={MissingScopeAction.DISABLE}
                                                                  scope={{
                                                                    scope: 'iph:read',
                                                                    superScope: ['iph:admin'],
                                                                  }}
                                                                >
                                                                  <Button
                                                                    className='px-2 py-2'
                                                                    variant='primaryGhost'
                                                                    onClick={() => handleConfigureClicked(aiFeaturePreset)}
                                                                    disabled={copied || !meetingTemplateId}
                                                                  >
                                                                    <i className='bi bi-gear' />
                                                                  </Button>
                                                                </ScopedBlock>
                                                              ) : null}
                                                              <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={MEETING_TEMPLATE_SCOPE}>
                                                                <Button
                                                                  className='px-2 py-2'
                                                                  variant='primaryGhost'
                                                                  onClick={() => handleDeleteMeetingTemplatePreset(aiFeaturePresetId)}
                                                                >
                                                                  <i className='bi bi-trash' />
                                                                </Button>
                                                              </ScopedBlock>
                                                              {meetingTemplatePresetCollapsible(aiFeaturePresetId) ? (
                                                                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={MEETING_TEMPLATE_SCOPE}>
                                                                  <Button
                                                                    className='px-2 py-2'
                                                                    variant='primaryGhost'
                                                                    onClick={() => handleCollapseMeetingTemplatePreset(aiFeaturePresetId)}
                                                                  >
                                                                    <i className='bi bi-chevron-double-left' />
                                                                  </Button>
                                                                </ScopedBlock>
                                                              ) : null}
                                                              {meetingTemplatePresetExpandable(aiFeaturePresetId) ? (
                                                                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={MEETING_TEMPLATE_SCOPE}>
                                                                  <Button
                                                                    className='px-2 py-2'
                                                                    variant='primaryGhost'
                                                                    onClick={() => handleExpandMeetingTemplatePreset(aiFeaturePresetId)}
                                                                  >
                                                                    <i className='bi bi-chevron-double-right' />
                                                                  </Button>
                                                                </ScopedBlock>
                                                              ) : null}
                                                            </div>
                                                          </Col>
                                                        </Row>
                                                      </Container>
                                                    </Col>
                                                  </Row>
                                                </Container>
                                              )}
                                            </Draggable>
                                            {provided.placeholder}
                                          </Col>
                                        )}
                                      </Droppable>
                                    )
                                  })}
                                </Row>
                              </Container>
                            )
                          })}
                        </Col>
                      </Row>
                    </DragDropContext>
                    <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={MEETING_TEMPLATE_SCOPE}>
                      <Row>
                        <Col className='p-0 d-flex align-items-center justify-content-center'>
                          <Button className='w-100 label-medium bold addPresetButton' variant='primaryGhost' onClick={handleAddMeetingTemplatePreset}>
                            <i className='bi bi-gear pe-2' />
                            Manage presets
                          </Button>
                        </Col>
                      </Row>
                    </ScopedBlock>
                  </Tab>
                  <Tab className='mt-5' eventKey='forms' title={formTabTitle} disabled={!formEnabled}>
                    <Row className='mb-2 zocks-mt-3'>
                      <Col className='p-0 d-flex gap-2 align-items-center'>
                        <h4>Forms {`(${forms.length}/${maxFormsInMeetingTemplate})`}</h4>
                      </Col>
                    </Row>
                    <Row className='p-0 d-flex align-items-center'>
                      <Col className='p-0 my-3'>
                        <AlertContainer>
                          <i className='bi bi-exclamation-circle-fill' />
                          <div className='paragraph-medium'>
                            Adding new forms won't recalculate your existing meetings. You will be able to manually run the newly added forms on the existing
                            meetings.
                          </div>
                        </AlertContainer>
                      </Col>
                    </Row>
                    <Row className='my-4'>{formData ? forms.map((form: AiFeature) => <FormListItem key={form.id} form={form} />) : <></>}</Row>
                    <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={MEETING_TEMPLATE_SCOPE}>
                      <Row>
                        <Col className='p-0 d-flex align-items-center justify-content-center'>
                          <Button className='w-100 label-medium bold addPresetButton' variant='primaryGhost' onClick={handleAddForm} disabled={!formEnabled}>
                            <i className='bi bi-gear pe-2' />
                            Manage forms
                          </Button>
                        </Col>
                      </Row>
                    </ScopedBlock>
                  </Tab>
                </Tabs>
              </Col>
            </Row>
            <Row>
              <Col className='d-flex align-items-center p-0 mt-4 justify-content-lg-end gap-3'>
                <Button variant='secondary' onClick={handleCancel}>
                  Cancel
                </Button>
                <ScopedBlock
                  missingScopeAction={MissingScopeAction.DISABLE}
                  scope={{
                    scope: meetingTemplateId ? 'meeting_template:update' : 'meeting_template:create',
                    superScope: ['meeting_template:admin'],
                  }}
                >
                  <Button
                    variant='primary'
                    onClick={handleSaveButtonClicked}
                    disabled={!name || (!fromSystemMeetingTemplate && !copied && !meetingTemplateChanged) || !activePresetCount}
                  >
                    <i className='bi bi-check-lg' />
                    Save
                  </Button>
                </ScopedBlock>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </>
  )
}

export const AIInfoTooltip = () => {
  return (
    <OverlayTrigger
      placement='top'
      overlay={
        <BootstrapTooltip className='zocks-text-gray-500'>
          Your <span className='aiAssistantText'>AI assistant</span> can suggest a matching Meeting Type to your upcoming meetings by looking into your previous
          meetings, meeting type settings and compare it to the details of your upcoming meeting.
        </BootstrapTooltip>
      }
    >
      <i className='bi bi-info-square ms-2' />
    </OverlayTrigger>
  )
}
